/**
 * Support page. Deliberately an email address, not a form.
 *
 * Several places already linked to /contact/ (the payment success page, the cancelled-
 * checkout page, the "contact sales" branch) and every one of them 404'd — this is the page
 * they were all pointing at. A form needs an inbox we don't have; a mailto goes straight to
 * an address already proven to deliver.
 *
 * Styled with the shared brand CSS so it matches the flagship and plugin pages.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { EMO_EMAIL } from "../constants";
import { ROUTES } from "../../constants/routes";
import { brandPageCss } from "../brand/brandPageCss";
import { BrandNav, BrandFooter, MailSvg, useBrandTheme } from "../brand/BrandChrome";
import {
  CONTACT_EYEBROW,
  CONTACT_TITLE,
  CONTACT_SUBTITLE,
  CONTACT_CTA,
  CONTACT_RESPONSE_NOTE,
  CONTACT_INCLUDE_HEADING,
  CONTACT_INCLUDE_INTRO,
  CONTACT_INCLUDE_ITEMS,
  CONTACT_SELF_SERVE_HEADING,
  CONTACT_SELF_SERVE,
} from "./contactConstants";

const SCOPE = "ccct";

const ContactPage: React.FC = () => {
  const navigate = useNavigate();
  const [theme, toggleTheme] = useBrandTheme();

  return (
    <div className={SCOPE} data-theme={theme}>
      <style>{brandPageCss(SCOPE) + PAGE_CSS}</style>

      <BrandNav theme={theme} onToggleTheme={toggleTheme}>
        <button className="linkbtn hide-sm" onClick={() => navigate(ROUTES.PRICING)}>
          Pricing
        </button>
      </BrandNav>

      <div className="wrap hero">
        <span className="eyebrow">{CONTACT_EYEBROW}</span>
        <h1>{CONTACT_TITLE}</h1>
        <p className="sub">{CONTACT_SUBTITLE}</p>

        <div className="cta">
          <a className="btn btn-cherry btn-lg" href={`mailto:${EMO_EMAIL}`}>
            <MailSvg />
            {CONTACT_CTA}
          </a>
        </div>
        <p className="fine">{CONTACT_RESPONSE_NOTE}</p>
      </div>

      <section className="wrap">
        <div className="sec-head">
          <h2>{CONTACT_INCLUDE_HEADING}</h2>
          <p>{CONTACT_INCLUDE_INTRO}</p>
        </div>
        <ul className="include">
          {CONTACT_INCLUDE_ITEMS.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="wrap">
        <div className="sec-head">
          <h2>{CONTACT_SELF_SERVE_HEADING}</h2>
        </div>
        <div className="quick">
          {CONTACT_SELF_SERVE.map((item) => (
            <button
              className="card qcard"
              key={item.label}
              onClick={() => navigate(item.route)}
            >
              <span className="qlabel">{item.label}</span>
              <span className="qnote">{item.note}</span>
            </button>
          ))}
        </div>
      </section>

      <BrandFooter>
        <a href={`mailto:${EMO_EMAIL}`}>{EMO_EMAIL}</a>
      </BrandFooter>
    </div>
  );
};

const PAGE_CSS = `
.ccct .include{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:13px}
.ccct .include li{position:relative;padding-left:26px;color:var(--muted);font-size:15px}
.ccct .include li::before{content:"";position:absolute;left:6px;top:9px;width:7px;height:7px;border-radius:50%;background:var(--cherry)}
.ccct .quick{display:grid;grid-template-columns:1fr 1fr;gap:16px}
.ccct .qcard{font-family:inherit;text-align:left;cursor:pointer;display:flex;flex-direction:column;gap:6px;transition:border-color .2s ease, transform .14s ease}
.ccct .qcard:hover{border-color:var(--cherry);transform:translateY(-2px)}
.ccct .qlabel{font-size:16px;font-weight:700;color:var(--ink)}
.ccct .qnote{font-size:14px;color:var(--muted)}
@media (prefers-reduced-motion: reduce){ .ccct .qcard:hover{transform:none} }
@media (max-width:560px){ .ccct .quick{grid-template-columns:1fr} }
`;

export default ContactPage;
